import { useIntl } from 'react-intl';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  FilterNames,
  TradeContext,
  TradeState,
  TradeStateName
} from '../../../_shared/types/trade';
import { objectToQueryString } from '../../../_shared/utils/url';
import { localStorageService } from '../../../services/StorageService';
import { CacheKeys } from '../../../_shared/constants/filters';
import {
  setActiveFilter,
  setActiveNetwork
} from '../../../redux/slices/filters';
import FilterService from '../../../services/FilterService';
import useCurrentTradeContext from '../../../_shared/hooks/trade/useCurrentTradeContext';
import stateRoutes from '../../../_shared/types/stateRoutes';
import ListPageFooter from '../../../_shared/components/ListPageFooter';
import { COOKIES } from '../../../_shared/constants/cookies';
import { getCookie } from '../../../_shared/utils/cookies';
import scrollToTop from '../../../_shared/helpers/scrollToTop';
import ompStateRoutes from '../../../_shared/types/ompStateRoutes';

const propTypes = {
  showFooterLabel: PropTypes.bool,
  isEditingFilters: PropTypes.bool,
  activeList: PropTypes.objectOf(PropTypes.any),
  previewCriteria: PropTypes.objectOf(PropTypes.object),
  tradeState: PropTypes.number.isRequired,
  numberOfTrades: PropTypes.number,
  activeNetwork: PropTypes.string,
  setIsEditingFilters: PropTypes.func,
  handleNavigateToTradeUri: PropTypes.func
};

const defaultProps = {
  showFooterLabel: true,
  isEditingFilters: false,
  activeList: null,
  previewCriteria: {},
  numberOfTrades: null,
  activeNetwork: undefined,
  setIsEditingFilters: undefined,
  handleNavigateToTradeUri: undefined
};

const TradeListFooter = ({
  tradeState,
  activeList,
  previewCriteria,
  activeNetwork,
  showFooterLabel,
  numberOfTrades,
  isEditingFilters,
  setIsEditingFilters,
  isOmp = false,
  handleNavigateToTradeUri
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const tradeContext = useCurrentTradeContext();
  const intl = useIntl();

  const showDMPWording =
    getCookie(COOKIES.IS_DMP_CHECKOUT_ENABLED) === 'true' || isOmp === true;

  // "Check in {state}" buttons.
  const footerButtonMappings = {
    [TradeContext.Buy]: {
      [TradeState.upcoming]: [TradeState.active],
      [TradeState.active]: [TradeState.upcoming],
      [TradeState.pending]: showDMPWording
        ? [TradeState.closing, TradeState.purchase]
        : [TradeState.closing, TradeState.arrived],
      [TradeState.closing]: [TradeState.active]
    },
    [TradeContext.Sell]: {
      [TradeState.upcoming]: [TradeState.active],
      [TradeState.active]: [TradeState.upcoming],
      [TradeState.pending]: [TradeState.closing, TradeState.arrived],
      [TradeState.closing]: [TradeState.arrived]
    }
  };

  const isFilterApplied =
    activeList &&
    (previewCriteria?.criteria || activeList?.id !== FilterNames.all.id);

  const updateNetworkOption = state => {
    if (!isFilterApplied) {
      return;
    }
    const networkCacheLocal =
      localStorageService.getItem(CacheKeys.selectedNetworkOption) ?? {};
    networkCacheLocal[state] = activeNetwork;

    localStorageService.setItem(
      CacheKeys.selectedNetworkOption,
      networkCacheLocal
    );

    dispatch(setActiveNetwork(networkCacheLocal));
  };

  const getPathByTradeState = state => {
    if (isOmp === true) {
      const ompRoute = ompStateRoutes[tradeContext][state];
      if (ompRoute) {
        return ompRoute;
      }
    }
    return stateRoutes[tradeContext][state];
  };

  const goToTradeState = state => {
    updateNetworkOption(state);
    if (handleNavigateToTradeUri) {
      handleNavigateToTradeUri({
        pathname: getPathByTradeState(state),
        search: objectToQueryString({ listId: activeList?.id }),
        state: { keepFilters: Boolean(isEditingFilters) }
      });
    } else {
      history.push({
        pathname: getPathByTradeState(state),
        search: objectToQueryString({ listId: activeList?.id }),
        state: { keepFilters: Boolean(isEditingFilters) }
      });
    }
    scrollToTop(isOmp);
  };

  // Force clear filter to all
  const clearFilters = () => {
    history.push({
      search: objectToQueryString({ listId: FilterNames.all.id })
    });
    dispatch(setActiveFilter(FilterNames.all));
    setIsEditingFilters(false);
    FilterService.setCachedTradeFilterId(tradeState, FilterNames.all.id);
  };

  const getLabelMoreByState = state => {
    switch (state) {
      case TradeState.upcoming:
        return intl.formatMessage({ id: 'list.footer.buy.looking' });
      case TradeState.active:
        return intl.formatMessage({ id: 'list.footer.buy.looking' });
      case TradeState.pending:
        if (tradeContext === TradeContext.Buy) {
          return showDMPWording
            ? intl.formatMessage({ id: 'list.footer.buy.looking.dmp.pending' })
            : intl.formatMessage({ id: 'list.footer.buy.looking.pending' });
        }
        return intl.formatMessage({ id: 'list.footer.sell.looking.pending' });

      case TradeState.closing:
        if (tradeContext === TradeContext.Buy) {
          return intl.formatMessage({ id: 'list.footer.buy.looking' });
        }
        return intl.formatMessage({ id: 'list.footer.sell.looking.closing' });

      default:
        return null;
    }
  };

  const getTradeStateFooter = state => {
    switch (state) {
      case TradeState.upcoming:
        return isFilterApplied
          ? intl.formatMessage(
              {
                id: 'list.footer.buy.upcoming.filtered'
              },
              {
                tradeCount: numberOfTrades
              }
            )
          : intl.formatMessage({ id: 'list.footer.buy.upcoming' });
      case TradeState.active:
        return isFilterApplied
          ? intl.formatMessage(
              {
                id: 'list.footer.buy.active.filtered'
              },
              {
                tradeCount: numberOfTrades
              }
            )
          : intl.formatMessage({ id: 'list.footer.buy.active' });
      case TradeState.pending:
        return intl.formatMessage({ id: 'list.footer.buy.pending' });
      case TradeState.closing:
        return intl.formatMessage({ id: 'list.footer.buy.closing' });
      default:
        return null;
    }
  };

  const getButtons = () => {
    const buttons = [];

    if (isFilterApplied) {
      buttons.push({
        text: intl.formatMessage({ id: 'list.filter.clear' }),
        onClick: () => {
          clearFilters();
        },
        dataTestId: 'pageFooterClear'
      });
    }

    if (tradeContext) {
      footerButtonMappings[tradeContext][tradeState].forEach(state => {
        const seeInTextId = isFilterApplied
          ? 'list.footer.see.in.filtered'
          : 'list.footer.see.in';

        const stateText = intl.formatMessage({
          id: `tradeState.${TradeStateName[state].toLowerCase()}`
        });

        buttons.push({
          text: intl.formatMessage(
            {
              id: seeInTextId
            },
            {
              tradeState: stateText
            }
          ),
          onClick: () => {
            goToTradeState(state);
          },
          dataTestId: `pageFooterButton-${state}`
        });
      });
    }

    return buttons;
  };

  return (
    <ListPageFooter
      showFooterLabel={showFooterLabel}
      label={getTradeStateFooter(tradeState)}
      secondaryLabel={getLabelMoreByState(tradeState)}
      buttons={getButtons()}
    />
  );
};

TradeListFooter.propTypes = propTypes;
TradeListFooter.defaultProps = defaultProps;
export default TradeListFooter;
